// Junta los números del negocio para Mis metas: ventas por día y por mes,
// gastos por categoría, productos estrella, clientas que no han vuelto, etc.
// Lo usan la tarjeta de Inicio, la página Mis metas y el plan con IA.
import {
  calcularAvance, sugerirMeta, sugerirPresupuesto, estadoPresupuesto, mejorDiaSemana, diasSinVender,
  ideasKontrol, coachDelDia, fechaYMD, mesAnterior, nombreMes,
} from './metas';
import { perfilesRecompra } from './recompra';
import { margenReal, gastosOperacion, pagosDeudas, numeroTranquilidad, saldoDeuda } from './finanzas';

const DIAS_DEFECTO = [1, 2, 3, 4, 5, 6];

export function armarResumen(datos, meta, mes, hoy = new Date()) {
  const diasVenta = meta?.dias_venta?.length ? meta.dias_venta : DIAS_DEFECTO;
  const porFecha = {}; // 'YYYY-MM-DD' → total vendido
  const porMes = {}; // 'YYYY-MM-01' → total
  const tickets = {}; // mes → Set de tickets
  const sumar = (f, monto) => {
    porFecha[f] = (porFecha[f] || 0) + monto;
    const m = `${f.slice(0, 7)}-01`;
    porMes[m] = (porMes[m] || 0) + monto;
  };
  (datos.ventas || []).forEach((v) => {
    const f = fechaYMD(new Date(v.vendido_en));
    sumar(f, Number(v.total || 0));
    const m = `${f.slice(0, 7)}-01`;
    (tickets[m] = tickets[m] || new Set()).add(String(v.ticket_id || `${v.vendido_en}`));
  });
  (datos.libres || []).forEach((i) => {
    const f = String(i.fecha).slice(0, 10);
    sumar(f, Number(i.monto || 0));
    const m = `${f.slice(0, 7)}-01`;
    (tickets[m] = tickets[m] || new Set()).add(`libre-${f}-${i.monto}`);
  });

  const vendido = porMes[mes] || 0;
  const avance = meta ? calcularAvance({ meta: meta.meta_ventas, vendido, mes, diasVenta, hoy }) : null;
  const anteriores = [1, 2, 3].map((n) => mesAnterior(mes, n));
  const ventasPorMes = anteriores.map((m) => ({ mes: m, nombre: nombreMes(m), total: porMes[m] || 0 }));
  const sugerida = sugerirMeta(ventasPorMes);

  // Gastos del mes por categoría y presupuesto sugerido.
  const gastadoPorCategoria = {};
  (datos.gastos || []).forEach((g) => {
    if (`${String(g.fecha).slice(0, 7)}-01` !== mes) return;
    const c = g.categoria || 'Otros';
    gastadoPorCategoria[c] = (gastadoPorCategoria[c] || 0) + Number(g.monto || 0);
  });
  const gastosMes = Object.values(gastadoPorCategoria).reduce((s, x) => s + x, 0);
  const presupuestoSugerido = sugerirPresupuesto(datos.gastos, anteriores);
  const presupuesto = estadoPresupuesto(meta?.presupuesto || {}, gastadoPorCategoria);

  // Ticket promedio de este mes y del anterior.
  const ticketDe = (m) => { const n = tickets[m]?.size || 0; return n ? (porMes[m] || 0) / n : 0; };

  // Productos estrella (últimos meses) por TIPO de producto ("BRASIER · 36D · NEGRO" → "Brasier"):
  // en tiendas de piezas únicas cada pieza se vende una vez, lo que importa es el tipo.
  // Su stock es la suma de las piezas de ese tipo que quedan.
  const tipoDe = (nombre) => String(nombre || '').split('·')[0].trim().toLowerCase();
  const bonito = (t) => t.charAt(0).toUpperCase() + t.slice(1);
  const stockPorTipo = new Map();
  (datos.productos || []).forEach((p) => {
    const t = tipoDe(p.nombre); if (!t) return;
    stockPorTipo.set(t, (stockPorTipo.get(t) || 0) + Math.max(0, Number(p.stock || 0)));
  });
  const unidades = new Map();
  (datos.ventas || []).forEach((v) => {
    const t = tipoDe(v.nombre_producto);
    if (!t) return;
    const u = unidades.get(t) || { nombre: bonito(t), unidades: 0, total: 0, stock: stockPorTipo.has(t) ? stockPorTipo.get(t) : null };
    u.unidades += Number(v.cantidad || 1); u.total += Number(v.total || 0);
    unidades.set(t, u);
  });
  const topProductos = [...unidades.values()].sort((a, b) => b.unidades - a.unidades).slice(0, 8);

  // Clientas que compraron antes de este mes y no han vuelto.
  const [ma, mm] = mes.split('-').map(Number);
  const clientesSinVolver = perfilesRecompra(datos.clientes || [], (datos.ventas || []).filter((v) => v.cliente_id), new Date(ma, mm - 1, 1), hoy)
    .filter((p) => !p.volvio).length;

  // Ayer y el día normal (promedio de los días con venta de los últimos 30 días).
  const ayer = fechaYMD(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - 1));
  const ult30 = [];
  for (let i = 1; i <= 30; i += 1) {
    const d = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - i);
    if (diasVenta.includes(d.getDay())) ult30.push(porFecha[fechaYMD(d)] || 0);
  }
  const promedioDiario = ult30.length ? ult30.reduce((s, x) => s + x, 0) / ult30.length : 0;

  const sinVentas = Object.keys(porFecha).length === 0;
  const productosConStock = (datos.productos || []).filter((p) => Number(p.stock || 0) > 0).length;

  // 🧭 Asesor financiero: margen, número de tranquilidad y deudas.
  const deudas = datos.deudas || [];
  const margen = margenReal(datos.ventas, datos.libres);
  const operacion = gastosOperacion(meta?.presupuesto, gastadoPorCategoria);
  const pagosDeuda = pagosDeudas(deudas, meta?.presupuesto);
  const finanzas = {
    margen, operacion, pagosDeuda,
    numero: numeroTranquilidad({ operacion, deudas: pagosDeuda, margen, diasVenta: avance?.diasVentaTotal || 26 }),
    deudaTotal: deudas.reduce((s2, d) => s2 + saldoDeuda(d).total, 0),
    deudaUsdSinCambio: deudas.reduce((s2, d) => s2 + (saldoDeuda(d).faltaCambio ? Number(d.saldo_usd || 0) : 0), 0),
    nDeudas: deudas.length,
  };
  const base = {
    avance, presupuesto, clientesSinVolver, topProductos,
    mejorDia: mejorDiaSemana(porFecha), diasSinVender: sinVentas ? 0 : diasSinVender(porFecha, diasVenta, hoy),
    ticketProm: ticketDe(mes), ticketPromAnterior: ticketDe(anteriores[0]),
    pedidosPendientes: datos.pedidosPendientes || 0, sinVentas,
    vendidoAyer: porFecha[ayer] ?? 0, promedioDiario, finanzas,
  };
  return {
    ...base, mes, diasVenta, porFecha, ventasPorMes, sugerida, gastadoPorCategoria, gastosMes,
    presupuestoSugerido, productosConStock, categoriasGasto: (datos.categoriasGasto || []).map((c) => c.nombre),
  };
}

export const ideasDe = (r, simbolo) => ideasKontrol(r, simbolo);
export const coachDe = (r, simbolo, pendientesHoy = 0) => coachDelDia({ ...r, pendientesHoy }, simbolo);

// Lo que se le manda a la IA: solo números y nombres (sin datos de clientas).
export function resumenParaIA(r, { negocio, meta, simbolo, estrategiasActuales = [], nota = '' }) {
  return {
    negocio: { nombre: negocio?.nombre, tipo: negocio?.rubro || null, moneda: simbolo },
    mes: nombreMes(r.mes),
    meta_del_mes: meta?.meta_ventas || null,
    para_que_la_quiero: meta?.motivo || null,
    dias_que_vende: r.diasVenta,
    como_va: r.avance ? {
      vendido: Math.round(r.avance.vendido), porcentaje: r.avance.pct, estado: r.avance.estado,
      falta: Math.round(r.avance.falta), dias_de_venta_que_quedan: r.avance.restantes, por_dia_para_llegar: Math.round(r.avance.porDiaRestante),
    } : null,
    ventas_meses_anteriores: r.ventasPorMes.map((x) => ({ mes: x.nombre, total: Math.round(x.total) })),
    venta_promedio_por_compra: Math.round(r.ticketProm), venta_promedio_mes_anterior: Math.round(r.ticketPromAnterior),
    mejor_dia: r.mejorDia?.nombre || null,
    dias_seguidos_sin_vender: r.diasSinVender,
    productos_mas_vendidos: r.topProductos.map((p) => ({ nombre: p.nombre, unidades: p.unidades, stock: p.stock })),
    productos_con_stock: r.productosConStock,
    gastos_del_mes: Math.round(r.gastosMes),
    presupuesto_gastos: r.presupuesto.filter((x) => x.presupuesto > 0 || x.gastado > 0).map((x) => ({ categoria: x.categoria, presupuesto: x.presupuesto, gastado: Math.round(x.gastado) })),
    clientas_que_no_han_vuelto_este_mes: r.clientesSinVolver,
    pedidos_del_catalogo_sin_atender: r.pedidosPendientes,
    empieza_de_cero: r.sinVentas,
    estrategias_que_ya_tiene: estrategiasActuales.map((e) => e.titulo),
    lo_que_quiere_lograr: nota || null,
    finanzas: r.finanzas ? {
      de_cada_100_vendido_le_quedan: r.finanzas.margen ? Math.round(r.finanzas.margen * 100) : null,
      gastos_del_negocio_al_mes: Math.round(r.finanzas.operacion),
      pagos_de_deudas_al_mes: Math.round(r.finanzas.pagosDeuda),
      necesita_vender_al_mes_para_cubrir_todo: r.finanzas.numero ? Math.round(r.finanzas.numero.ventas) : null,
      deudas_total: Math.round(r.finanzas.deudaTotal), deudas_en_dolares_sin_convertir: Math.round(r.finanzas.deudaUsdSinCambio),
      cuantas_deudas: r.finanzas.nDeudas,
    } : null,
  };
}
